import { errorReporter } from '@/services/errorReportingService';

/**
 * Dotted numeric only — no `v` prefix, no `-beta` suffix.
 *
 * Canonical format from the cross-repo contract
 * (notego-crossreference/coordination/contracts/2026-09-03-campaign-release-version-gate.md).
 * Admin validates the same expression at the authoring end, and the desktop app
 * applies the same one when it gates its own banners.
 */
const VERSION_PATTERN = /^\d+(\.\d+)*$/;

/** Whether a string is a usable dotted-numeric version. Trims first. */
export const isValidVersion = (value: string | null | undefined): boolean => VERSION_PATTERN.test((value ?? '').trim());

/**
 * Compare two dotted numeric versions segment by segment.
 *
 * NEVER compare these as strings: lexicographically "1.2.9" > "1.2.10", which is the exact
 * bug this whole feature exists to avoid. A missing segment counts as 0, so "1.2" equals
 * "1.2.0" and "1.2" is less than "1.2.1".
 *
 * Both arguments must already be format-validated with `isValidVersion`.
 *
 * @returns Negative when a < b, positive when a > b, 0 when equal.
 */
export const compareVersions = (a: string, b: string): number => {
  const left = a.trim().split('.').map(Number);
  const right = b.trim().split('.').map(Number);
  const length = Math.max(left.length, right.length);

  for (let i = 0; i < length; i += 1) {
    const diff = (left[i] ?? 0) - (right[i] ?? 0);
    if (diff !== 0) return diff;
  }
  return 0;
};

/**
 * Whether a banner or campaign clears the optional release-version gate.
 *
 * An admin can tag a message with the desktop version it announces; a user already on that
 * version should not keep seeing "v1.2.48 is out". The comparison lives entirely on the
 * client — the API deliberately never tries to infer which build is asking.
 *
 * Rule order:
 *   1. No `releaseVersion` ⇒ not gated at all. The overwhelmingly common case.
 *   2. A malformed `releaseVersion` fails OPEN. A typo in the admin form must not silently
 *      swallow a message whose absence nobody can then explain.
 *   3. An unknown or malformed `installedVersion` fails OPEN. **This is a deliberate
 *      divergence from the desktop app's identical-looking gate, which fails CLOSED.**
 *      See below — do not "fix" it to match.
 *   4. Otherwise: show only while the installed version is strictly lower.
 *
 * Why rule 3 is inverted here: on the desktop, an unknown installed version means the IPC
 * has not resolved yet, and the app is definitionally installed, so hiding for a moment is
 * right. On the web, the overwhelming majority of visitors have no desktop app at all and
 * will never have a version. Failing closed would hide every release announcement from
 * everyone who has not installed the desktop app — the exact opposite of what a release
 * banner is for, since those are the people most likely to want the download. Web is the
 * ungated surface by design; it gates only when it actually knows a version to gate on.
 *
 * @param releaseVersion Version the message announces (from the campaign/banner doc).
 * @param installedVersion Desktop version this user last reported, if any.
 * @returns True when the message may render.
 */
export function passesVersionGate(
  releaseVersion: string | null | undefined,
  installedVersion: string | null | undefined,
): boolean {
  const release = (releaseVersion ?? '').trim();
  if (!release) return true;

  // Trimmed before testing, matching every other reader on the platform. Whitespace is a
  // formatting artifact, not a malformed version: "1.0.0 " means 1.0.0 and must gate on it.
  //
  // This is NOT redundant with the writers validating. Firestore enforces no schema, so a
  // doc can be hand-edited in the console, restored from an export, or written by a tool
  // that does not exist yet, and docs predating any validation are still in the collection.
  // Never drop this on the grounds that every writer checks today.
  if (!VERSION_PATTERN.test(release)) {
    errorReporter.expected('Ignoring malformed campaign releaseVersion', undefined, {
      feature: 'app',
      operation: 'passesVersionGate',
    });
    return true;
  }

  const installed = (installedVersion ?? '').trim();
  if (!installed || !VERSION_PATTERN.test(installed)) return true;

  return compareVersions(installed, release) < 0;
}
